import { useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  InputAdornment,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import axios from "axios";
import dayjs, { Dayjs } from "dayjs";
import { useQuery } from "@tanstack/react-query";
import useAuth from "../../hooks/useAuth";
import { apiVariable } from "../../api/apiVariable";

interface DriverDailyDrivingTimeDto {
  driverId: string;
  driverName?: string | null;
  date: string;
  totalDrivingSeconds: number;
  totalDrivingHours: number;
}

const fetchHourlyDriversDailyDrivingTimes = async (
  token: string | null | undefined,
  startDate?: string,
  endDate?: string,
): Promise<DriverDailyDrivingTimeDto[]> => {
  if (!token) {
    throw new Error("Missing authentication token");
  }

  const response = await axios.get<DriverDailyDrivingTimeDto[]>(
    `${apiVariable}/getHourlyDriversDailyDrivingTimes`,
    {
      params: { startDate, endDate },
      headers: { Authorization: `Bearer ${token}` },
    },
  );

  return response.data;
};

const formatDrivingTime = (seconds: number, hours: number) => {
  if (!Number.isFinite(seconds) || seconds <= 0) {
    return "0h 0m";
  }

  const wholeHours = Math.floor(seconds / 3600);
  const minutes = Math.round((seconds % 3600) / 60);
  const hourLabel = Number.isFinite(hours) ? hours.toFixed(2) : wholeHours;

  return `${hourLabel} hrs • ${wholeHours}h ${minutes}m`;
};

const getNameParts = (name?: string | null) => {
  const trimmed = name?.trim() ?? "";
  if (!trimmed) {
    return { firstName: "", lastName: "", fullName: "Unknown driver" };
  }

  const parts = trimmed.split(/\s+/);
  const firstName = parts[0] ?? "";
  const lastName = parts.slice(1).join(" ");

  return {
    firstName,
    lastName,
    fullName: trimmed,
  };
};

const HourlyPaidDriversPage = () => {
  const { token } = useAuth();
  const [selectedStartDate, setSelectedStartDate] = useState<Dayjs>(
    dayjs().startOf("day"),
  );
  const [selectedEndDate, setSelectedEndDate] = useState<Dayjs>(
    dayjs().startOf("day"),
  );
  const [searchTerm, setSearchTerm] = useState("");

  const selectedStartDateKey = useMemo(
    () => selectedStartDate.format("YYYY-MM-DD"),
    [selectedStartDate],
  );

  const selectedEndDateKey = useMemo(
    () => selectedEndDate.format("YYYY-MM-DD"),
    [selectedEndDate],
  );

  const selectedDateLabel = useMemo(() => {
    const startLabel = selectedStartDate.format("MMMM D, YYYY");
    const endLabel = selectedEndDate.format("MMMM D, YYYY");

    if (selectedStartDate.isSame(selectedEndDate, "day")) {
      return startLabel;
    }

    return `${startLabel} - ${endLabel}`;
  }, [selectedStartDate, selectedEndDate]);

  const {
    data: drivers = [],
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ["hourly-paid-drivers", selectedStartDateKey, selectedEndDateKey],
    queryFn: () =>
      fetchHourlyDriversDailyDrivingTimes(
        token,
        selectedStartDateKey,
        selectedEndDateKey,
      ),
    enabled: Boolean(token),
  });

  const filteredDrivers = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    if (!normalizedSearch) {
      return drivers;
    }

    return drivers.filter((driver) => {
      const { firstName, lastName, fullName } = getNameParts(driver.driverName);
      return (
        firstName.toLowerCase().includes(normalizedSearch) ||
        lastName.toLowerCase().includes(normalizedSearch) ||
        fullName.toLowerCase().includes(normalizedSearch)
      );
    });
  }, [drivers, searchTerm]);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
        <CardContent>
          <Stack spacing={2}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 2,
              }}
            >
              <Box>
                <Typography variant="h5" fontWeight={700}>
                  Hourly Paid Drivers
                </Typography>
                <Typography color="text.secondary">
                  Driving time overview for {selectedDateLabel}
                </Typography>
              </Box>
              <Typography variant="body2" color="text.secondary">
                {filteredDrivers.length} driver
                {filteredDrivers.length === 1 ? "" : "s"} shown
              </Typography>
            </Box>
            <Divider />
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={2}
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <TextField
                fullWidth
                label="Search by first or last name"
                placeholder="Start typing a driver's name"
                value={searchTerm}
                onChange={(event) => setSearchTerm(event.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchRoundedIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
              />
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Start date"
                  value={selectedStartDate}
                  onChange={(newValue) => {
                    if (newValue) {
                      const nextStart = newValue.startOf("day");
                      setSelectedStartDate(nextStart);
                      if (selectedEndDate.isBefore(nextStart, "day")) {
                        setSelectedEndDate(nextStart);
                      }
                    }
                  }}
                  slotProps={{
                    textField: { fullWidth: true },
                  }}
                />
                <DatePicker
                  label="End date"
                  value={selectedEndDate}
                  minDate={selectedStartDate}
                  onChange={(newValue) => {
                    if (newValue) {
                      setSelectedEndDate(newValue.startOf("day"));
                    }
                  }}
                  slotProps={{
                    textField: { fullWidth: true },
                  }}
                />
              </LocalizationProvider>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ boxShadow: 2, borderRadius: 2 }}>
        <CardContent>
          {isLoading && (
            <Box
              sx={{
                flexWrap: { xs: "wrap", lg: "nowrap" },
                display: "flex",
                justifyContent: "center",
                py: 6,
              }}
            >
              <CircularProgress />
            </Box>
          )}

          {isError && (
            <Alert severity="error">{(error as Error).message}</Alert>
          )}

          {!isLoading && !isError && filteredDrivers.length === 0 && (
            <Alert severity="info">
              No hourly paid drivers found for this date range.
            </Alert>
          )}

          {!isLoading && !isError && filteredDrivers.length > 0 && (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Driver</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell align="right">Total Driving</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDrivers.map((driver) => {
                    const { fullName } = getNameParts(driver.driverName);
                    const initials = fullName
                      .split(" ")
                      .filter(Boolean)
                      .slice(0, 2)
                      .map((part) => part.charAt(0).toUpperCase())
                      .join("");

                    return (
                      <TableRow key={driver.driverId} hover>
                        <TableCell>
                          <Stack
                            flexWrap={{ xs: "wrap", lg: "nowrap" }}
                            useFlexGap
                            direction="row"
                            spacing={2}
                            alignItems="center"
                          >
                            <Avatar sx={{ bgcolor: "primary.main" }}>
                              {initials || "?"}
                            </Avatar>
                            <Box>
                              <Typography fontWeight={600}>
                                {fullName}
                              </Typography>
                            </Box>
                          </Stack>
                        </TableCell>
                        <TableCell>
                          {dayjs(driver.date).format("MMM D, YYYY")}
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight={600}>
                            {formatDrivingTime(
                              driver.totalDrivingSeconds,
                              driver.totalDrivingHours,
                            )}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {driver.totalDrivingSeconds.toLocaleString()}{" "}
                            seconds
                          </Typography>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default HourlyPaidDriversPage;
